import { NavLink, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSiteBanner } from "../../hooks/useSiteBanner";
import { SITE_BANNER_HEIGHT } from "./SiteBanner";
import { LOGO_URL, getPortalTitleForRole } from "../../constants/brand";
import { SignOut, Gear, ShieldCheck, UserCircle } from "@phosphor-icons/react";
import { SecurityBadge } from "../shared/SecurityBadge";

export function Sidebar({ navItems, onOpenSettings, moduleName, moduleBackPath, mobileOpen, onMobileClose }) {
  const { user, logout } = useAuth();
  const banner = useSiteBanner();
  const topOffset = SITE_BANNER_HEIGHT(user, banner);
  const isClientType = user?.role === "client" || user?.role === "affiliate";

  return (
    <>
      {mobileOpen && (
        <div
          data-testid="mobile-sidebar-overlay"
          onClick={onMobileClose}
          className="fixed inset-0 bg-black/60 z-[55] md:hidden"
        />
      )}
      <aside
        title="Main navigation — every page for your role lives here. On a phone, tap the menu icon in the top bar to open this, tap a link to go there, or tap outside/tap the menu icon again to close it."
        className={`w-64 min-h-screen bg-igc-bg border-r border-igc-gold/15 flex flex-col fixed left-0 z-[60] transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        style={{ top: topOffset, height: `calc(100vh - ${topOffset}px)` }}
        data-testid="sidebar"
      >
      <div className="flex flex-col items-center py-8 border-b border-igc-gold/15">
        <img src={LOGO_URL} alt="Iron Gate Consulting" className="w-20 h-20 object-contain mb-3 igc-glow" />
        <p data-testid="sidebar-brand-title" className="font-serif text-igc-cream text-sm tracking-wide text-center leading-tight">{getPortalTitleForRole(user?.role)}</p>
        <SecurityBadge variant="sidebar" />
        {moduleName && (
          moduleBackPath ? (
            <Link to={moduleBackPath} data-testid="sidebar-module-back-link" title="Go back to your main dashboard home screen" className="text-igc-goldText text-[10px] uppercase tracking-widest mt-2 hover:text-igc-goldHover transition-colors">
              ← {moduleName}
            </Link>
          ) : (
            <p className="text-igc-goldText text-[10px] uppercase tracking-widest mt-2">{moduleName}</p>
          )
        )}
      </div>

      <nav className="flex-1 py-8 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testId}
            onClick={onMobileClose}
            title={`Go to the ${item.label} page`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-8 py-3 text-sm tracking-wide transition-all ${
                isActive
                  ? "border-l-2 border-igc-gold text-igc-goldText bg-igc-surface shadow-[inset_0_0_20px_rgba(212,175,55,0.08)]"
                  : "border-l-2 border-transparent text-igc-muted hover:text-igc-cream hover:bg-igc-surface/50"
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-6 border-t border-igc-gold/10 space-y-3">
        <div className="text-xs text-igc-muted">
          <p className="text-igc-cream text-sm" data-testid="sidebar-user-id">{user?.client_id || user?.display_name}</p>
        </div>
        <Link
          to="/security"
          data-testid="sidebar-security-center-link"
          title="Opens the Security & Compliance page — explains how your data, calls, and account are protected, and shows the security safeguards active on the portal."
          onClick={onMobileClose}
          className="flex items-center gap-2 text-igc-muted hover:text-igc-goldText transition-colors text-sm w-full"
        >
          <ShieldCheck size={16} weight="duotone" /> Security & Compliance
        </Link>
        {isClientType && (
          <Link
            to="/client/profile"
            data-testid="open-my-profile-button"
            title="Opens your profile page where you can update your own personal details."
            onClick={onMobileClose}
            className="flex items-center gap-2 text-igc-muted hover:text-igc-goldText transition-colors text-sm w-full"
          >
            <UserCircle size={16} weight="duotone" /> My Profile
          </Link>
        )}
        <button
          data-testid="open-settings-button"
          onClick={onOpenSettings}
          title="Opens Settings — change your password, notification preferences, and other account options."
          className="flex items-center gap-2 text-igc-muted hover:text-igc-goldText transition-colors text-sm w-full"
        >
          <Gear size={16} weight="duotone" /> Settings
        </button>
        <button
          data-testid="logout-button"
          onClick={logout}
          title="Signs you out of the portal completely and sends you back to the login screen. You'll need your username/rep code and password to log back in."
          className="flex items-center gap-2 text-igc-muted hover:text-igc-danger transition-colors text-sm w-full"
        >
          <SignOut size={16} weight="duotone" /> Sign Out
        </button>
      </div>
      </aside>
    </>
  );
}
